import moment from 'moment';
import FileSaver from 'file-saver';
import { CellContextMenuEvent, ColumnApi, ColumnState, GridApi, IGetRowsParams } from '@ag-grid-community/all-modules';
import { IDatasource } from '@ag-grid-community/core/dist/cjs/interfaces/iDatasource';
import { FieldState } from 'formstate';
import { IItemsList, Validators } from 'welltrax-ui-components';
import { cloneDeep, get, isDate, isFinite, isNumber, orderBy, trim, uniq, uniqueId } from 'lodash';
import { useEffect } from 'react';

import { IColumnType, IReplaceInValue, IViewTypes } from '../../../core/types/ProfileTypes';
import { ApplicationFeatureListType } from '../../../core/types/UserGroup';
import { intl } from '../../../core/utils/Intl';
import { getDatesObject, replaceAll, validateAndGetObject } from '../../../core/utils/utils';
import { checkIfNumber } from '../../../core/utils/utilsForm';
import { GlobalDynamicField } from '../components/dynamicField/types/types';
import { validateMaxValue, validateMinValue } from '../components/dynamicField/utils/DynamicFieldsUtil';
import { BasicListByTypeStore } from '../store/BasicListByTypeStore';
import { SetupStore } from '../store/SetupStore';
import authService from '../../../core/services/AuthServices';
import appStore from '../../../core/stores/AppStore';
import dropOffService from '../../setup/location/dropOff/services/DropOffService';
import pickUpService from '../../setup/location/pickUp/services/PickUpService';
import basicListByTypeService from '../services/BasicListByTypeService';
import driverGroupService from '../../setup/definitions/code/services/DriverGroupService';
import { Note } from '../../dispatch/components/dispatchLoad/types/DispatchBoardTypes';
import requirementGroupService from '../services/GroupService';
import profileService from '../../../core/services/ProfileService';

export const flattenMessages = (nestedMessages: any, prefix = '') => {
  return Object.keys(nestedMessages).reduce((messages: any, key: any) => {
    const value = nestedMessages[key];
    const prefixedKey = prefix ? `${prefix}.${key}` : key;

    if (typeof value === 'string') {
      messages[prefixedKey] = value;
    } else {
      Object.assign(messages, flattenMessages(value, prefixedKey));
    }
    return messages;
  }, {});
};

export const getInactiveStatus = (status: boolean) => (!status ? intl('app.statusInactive', '(INACTIVE)') : '');

export const getAccountNumber = (account: any) =>
  !!account?.extraColumns?.accountNumber ? `(${account?.extraColumns?.accountNumber})` : '';

export const getOperatorNumber = (operator: any) =>
  !!operator.extraColumns?.operatorNumber ? `(${operator.extraColumns?.operatorNumber})` : '';

export const getPickUpName = (basic: any) => {
  const pickUpNumber = get(basic, `extraColumns.pickUpDropOffNumber`) ?? get(basic, `leaseNumberAlphaNumeric`, '');
  const operator = get(basic, `extraColumns.PU_OPERATOR_NAME`, '');
  return `${basic?.description} ${pickUpNumber ? `(${pickUpNumber})` : ''} ${
    operator ? `(${operator})` : ''
  } ${getInactiveStatus(basic.status)} ${
    basic?.extraColumns?.IS_SUSPENDED ? intl('setup.locations.pickup.form.suspended', '(SUSPENDED)') : ''
  }`;
};

export const getPickUpContactInfo = (pickUp: any) => {
  const pickUpNumber = get(pickUp, `contact.pickUpDropOffNumber`) ?? get(pickUp, `leaseNumberAlphaNumeric`, '');
  const operator = get(pickUp, `operator.contact.fullName`, '');
  return `${pickUp?.contact.fullName} ${pickUpNumber ? `(${pickUpNumber})` : ''} ${
    operator ? `(${operator})` : ''
  } ${getInactiveStatus(pickUp.isActive)} ${
    pickUp.isSuspended ? intl('setup.locations.pickup.form.suspended', '(SUSPENDED)') : ''
  }`;
};

export const getDropOffName = (basic: any, withAccount = false) => {
  const dropOffNumberOrAccount = get(
    basic,
    `${!withAccount ? 'extraColumns.pickUpDropOffNumber' : 'extraColumns.DO_ACCOUNT_NAME'}`,
    ''
  );
  const operator = get(basic, `extraColumns.DO_OPERATOR_NAME`, '');
  return `${basic?.description} ${dropOffNumberOrAccount ? `(${dropOffNumberOrAccount})` : ''} ${
    operator ? `(${operator})` : ''
  } ${getInactiveStatus(basic.status)}`;
};

export const getDropOffNameRanked = (basic: any) => {
  const dropOffNumber = basic?.dropOffNumberAlphaNumeric || '';
  const operator = basic?.operator?.contact?.fullName || '';
  return `${basic?.contact?.fullName} ${dropOffNumber ? `(${dropOffNumber})` : ''} ${
    operator ? `(${operator})` : ''
  } ${getInactiveStatus(basic?.isActive)}`;
};

export const getPermission = (objectPermission: ApplicationFeatureListType | null) => {
  if (objectPermission === null) {
    return true;
  }
  if (!!objectPermission?.id && !!objectPermission?.internalName) {
    return !objectPermission.visible || !objectPermission.unlocked;
  }
  return;
};

export const getValueDynamicField = (
  field: Partial<GlobalDynamicField>,
  element?: any,
  allDynamicFields?: Partial<GlobalDynamicField>[]
) => {
  switch (field.type) {
    case 'CUSTOM_BOBTAIL':
    case 'CUSTOM_RECEIPT':
    case 'TEXT': {
      return field?.stringValue || '';
    }
    case 'CALCULATION':
    case 'CUSTOM_PRODUCT':
    case 'FEET_INCH_1$$4INCH':
    case 'CUSTOM_ODOMETER':
    case 'INVENTORY':
    case 'NUMERIC':
    case 'CUSTOM_TOP_GAUGE_GROSS_BARREL':
    case 'CUSTOM_BOTTOM_GAUGE_GROSS_BARREL': {
      return field?.numValue || (checkIfNumber(field?.stringValue) ? Number(field?.stringValue) : 0);
    }
    case 'CUSTOM_CONFIRMATION': {
      return element?.confirmationNumer;
    }
    case 'DROP_DOWN':
    case 'RADIO_BUTTON': {
      if (
        field &&
        `${field.selectionIndex}` &&
        field.options &&
        isNumber(field.selectionIndex) &&
        isFinite(field.selectionIndex) &&
        field.selectionIndex >= 0 &&
        field.selectionIndex < field.options.length &&
        field.options[field.selectionIndex]
      ) {
        return field.options[field.selectionIndex]?.optionId;
      }
      if (
        field &&
        !field.options &&
        allDynamicFields &&
        isNumber(field.selectionIndex) &&
        isFinite(field.selectionIndex) &&
        field.selectionIndex >= 0
      ) {
        const filtered = allDynamicFields?.filter(x => x.fieldName === field?.fieldName);
        if (filtered?.[0]) {
          return filtered[0].options[field.selectionIndex]?.optionId;
        }
      }
      return '';
    }
    case 'CHECK_BOX': {
      const values: any[] = [];
      const options = field?.options || [];
      if (options.length > 1) {
        options.forEach((opt: any) => {
          if (opt.selected) {
            values.push(opt?.optionId);
          }
        });
        if (!values.length && appStore.formState === 'CREATE') {
          const foundedItem = options.find((it: any) => it.optionName === field.defaultStringValue);
          if (!!foundedItem) {
            values.push(foundedItem.optionId);
          }
        }
        return values;
      }
      return get(options, '[0].selected', false);
    }
    case 'CUSTOM_TANK': {
      let value = '';
      if (element?.tanksList && element?.tanksList.length > 0 && element?.tanksList[0]) {
        value = `${
          element?.tanksList[0].id || (isNumber(element.tanksList[0].id) && `${element.tanksList[0].id}`) || ''
        }`;
      }
      return value || `${field?.numValue}` || '';
    }
    case 'DATE_TIME': {
      const date =
        (field?.dateTime && moment(`${field?.dateTime?.date} ${field?.dateTime?.time || '00:00'}`).toDate()) || null;
      return (isDate(date) && date) || null;
    }
    case 'DEPENDENCY_FORM': {
      return '';
    }
  }
  return '';
};

export const getDisplayValuesDyanamicFields = (
  field: Partial<GlobalDynamicField>,
  element?: any,
  load?: any,
  layoutFields?: Partial<GlobalDynamicField>[]
) => {
  switch (field.type) {
    case 'CUSTOM_RECEIPT':
    case 'TEXT': {
      return field?.stringValue || '';
    }
    case 'FEET_INCH_1$$4INCH':
    case 'CUSTOM_ODOMETER':
    case 'CALCULATION':
    case 'NUMERIC':
    case 'INVENTORY':
    case 'CUSTOM_TOP_GAUGE_GROSS_BARREL':
    case 'CUSTOM_BOTTOM_GAUGE_GROSS_BARREL': {
      return field?.numValue || (checkIfNumber(field?.stringValue) ? Number(field?.stringValue) : 0);
    }
    case 'CUSTOM_CONFIRMATION': {
      return element?.confirmationNumer;
    }
    case 'CUSTOM_CONTRACTOR': {
      return load?.contractor?.contact?.fullName || '-';
    }
    case 'CUSTOM_PRODUCT': {
      return element?.productType?.description || '-';
    }
    case 'DROP_DOWN':
    case 'RADIO_BUTTON': {
      if (
        field &&
        `${field.selectionIndex}` &&
        field.options &&
        isNumber(field.selectionIndex) &&
        isFinite(field.selectionIndex) &&
        field.selectionIndex >= 0 &&
        field.selectionIndex < field.options.length &&
        field.options[field.selectionIndex]
      ) {
        return field.options[field.selectionIndex]?.optionName;
      } else if (
        layoutFields &&
        isNumber(field.selectionIndex) &&
        isFinite(field.selectionIndex) &&
        field.selectionIndex >= 0
      ) {
        const layoutField = layoutFields.filter(x => x.fieldName === field.fieldName)[0];
        return layoutField.options[field.selectionIndex]?.optionName;
      }
      return '';
    }
    case 'CHECK_BOX': {
      const values: string[] = [];
      const options = field?.options || [];
      options.forEach((opt: any) => {
        if (opt.selected) {
          values.push(opt?.optionName);
        }
      });
      return values.length > 0 ? values.join(',') : '';
    }
    case 'CUSTOM_TANK': {
      const tanks = element?.tanksList || [];
      return tanks.find((el: any) => el.isSelectedInLoad)?.tankNumber || '-';
    }
    case 'DATE_TIME': {
      const stringDate = field.stringValue;
      if (stringDate && moment(stringDate).isValid()) {
        return moment(stringDate).format('MM/DD/YYYY hh:mm a');
      }
      const date = (field?.dateTime && moment(`${field?.dateTime?.date} ${field?.dateTime?.time}`).toDate()) || null;
      return (moment(date).isValid() && moment(date).format('MM/DD/YYYY hh:mm a')) || '';
    }
    case 'DEPENDENCY_FORM': {
      const labelValues: any[] = [];
      field?.innerDynamicFieldList?.forEach((row: any) => {
        const innerValues: any[] = [];
        row?.forEach((innerField: any) => {
          innerValues.push(getDisplayValuesDyanamicFields(innerField, null, null, layoutFields));
        });
        labelValues.push(innerValues.join(', '));
      });
      return labelValues?.join('; ');
    }
  }
  return field?.stringValue || '';
};

export const replaceInValueElement = (result: string, typeColumn?: IColumnType, replace?: IReplaceInValue) => {
  if (typeColumn === 'STRING' && replace) {
    return replaceStringToChar(result, replace.from, replace.to);
  }
  return result;
};

export const replaceStringToChar = (object: string, from: string, to: string) => {
  const exp = new RegExp(from, 'g');
  return object.replace(exp, to);
};

export const parseDateTimeToForm = (dateTime: any, type: 'DATE' | 'TIME' | 'DATE_TIME'): any => {
  switch (type) {
    case 'DATE':
      if (dateTime?.date) {
        const { date } = dateTime;
        return date;
      }
      break;
    case 'TIME':
      if (dateTime?.time) {
        const { time } = dateTime;
        return time;
      }
      break;
    case 'DATE_TIME':
      if (dateTime?.date && dateTime?.time) {
        const { date, time } = dateTime;
        return `${date} ${time}`;
      }
      break;
  }

  return null;
};

export const mapToSelectValue = (data: any[], callback?: (item: any) => any) => {
  return data.map((item: any) => {
    return (
      callback?.(item) ?? {
        value: item.id,
        label: item.description
      }
    );
  });
};

export const createUrlToDownloadReport = (reportFileName: string, sourceType: string, extension: string) => {
  const user = authService.getUser();
  if (user) {
    const challenge: string = authService.createToken(user);
    const requester: string = validateAndGetObject(user, 'alias.requester');
    const sourceTypeReplaced = replaceAll(sourceType, ' ', '%20');
    return `reportTitle=${sourceTypeReplaced}&type=report&displayName=${sourceTypeReplaced}&filename=${reportFileName}&challenge=${challenge}&requester=${requester}&extension=${extension}`;
  }
  return '';
};

export const handleReportDownload = (fileName: string, sourceType: string) => {
  const url = `${process.env.REACT_APP_API_URL}document/download?${createUrlToDownloadReport(
    fileName,
    sourceType,
    'pdf'
  )}`;
  FileSaver.saveAs(url, sourceType);
};

export const useRemoveFieldsValidationWhen = (fields: FieldState<any>[], condition: boolean, watch: any[]) =>
  useEffect(() => {
    if (condition) {
      fields.forEach(field => {
        field?.validators?.(...[]);
      });
    }
  }, watch);

export const useAddRemoveFieldsValidationForNumericWhen = (
  field: FieldState<any>,
  condition: boolean,
  item: any,
  props: any,
  watch: any[]
) =>
  useEffect(() => {
    if (condition) {
      field?.validators?.(...[]);
    } else {
      field?.validators?.(
        ...[
          Validators.requiredIf(() => !!props.required),
          validateMaxValue(checkIfNumber)(item.maxValue, item.minValue),
          validateMinValue(checkIfNumber)(item.minValue, item.maxValue),
          ...(props.extraValidators || [])
        ]
      );
    }
  }, watch);

// method for handling double click events on grid
export const onDoubleClickGridRow = (store: SetupStore, permission?: ApplicationFeatureListType | null) => {
  if (!!permission && permission.editable) {
    if (store.element) {
      store.setOriginalElement(cloneDeep(store.element));
    }
    store.setFormState('EDIT');
  }
};

export const onCellContextMenu =
  (entityStore: any, gridApi: GridApi | null, single?: boolean, multiSelect = true) =>
  (params: CellContextMenuEvent) => {
    let newList: number[] = [];
    const clikedNode = params?.node?.data;
    params.api.getSelectedNodes()?.map((node: any) => {
      newList.push(node.data.id);
    });
    if (clikedNode?.id) {
      if (!newList.some(item => item === clikedNode.id)) {
        !single && entityStore.setElementClicked(clikedNode.id);
        if (uniq(newList).length > 1 && !single) {
          newList.push(clikedNode.id);
        } else {
          newList = [clikedNode.id];
        }
        if (gridApi) {
          gridApi.forEachNode((node: any) => {
            const nodeId = node?.data?.id;
            if (newList.some(item => item === nodeId)) {
              node.setSelected(true);
            } else {
              node.setSelected(false);
            }
          });
        }
      } else if (!multiSelect) {
        entityStore.setElementClicked(clikedNode.id);
      }
    }
  };

export const maxTime = () => {
  const max: Date = moment(moment().format('YYYY/MM/DD')).toDate();
  max.setHours(23, 59);
  return max;
};

/**
 * @description Method to sort arrays by string property
 * @param a String to compare
 * @param b String to compare
 */
export const sortArrayByStringProperty = (a: string, b: string) => {
  const nameA = a.toUpperCase(); // ignore upper and lowercase
  const nameB = b.toUpperCase(); // ignore upper and lowercase
  if (nameA < nameB) return -1;
  if (nameA > nameB) return 1;
  // names must be equal
  return 0;
};

export const commonInfiniteScroll = {
  rowBuffer: 0,
  rowModelType: 'infinite',
  paginationPageSize: 100,
  cacheOverflowSize: 2,
  maxConcurrentDatasourceRequests: 1,
  infiniteInitialRowCount: 1000,
  maxBlocksInCache: 10
};

export const isRedirectToNewDomain = (org: any): boolean => {
  return (
    !window.location?.href?.toString().toLowerCase().includes(org?.portalServerPath) && org?.isRedirectToOtherServer
  );
};

export const hasAuth0email = (user: any): boolean => {
  return user?.auth0email;
};

export const buildDataSourceToAssignUnAssignTable = (data: any[], sortModel?: Partial<ColumnState>[]): IDatasource => {
  return {
    rowCount: 0,
    getRows: (params: IGetRowsParams) => {
      const orderedDate = !sortModel?.length
        ? data
        : orderBy(
            data,
            sortModel.map(el => el?.colId),
            sortModel.map(el => (el?.sort === 'desc' ? 'desc' : 'asc'))
          );
      setTimeout(() => {
        const rowsThisPage = orderedDate.slice(params.startRow, params.endRow);
        let lastRow = -1;
        if (orderedDate.length <= params.endRow) {
          lastRow = orderedDate.length;
        }
        params.successCallback(rowsThisPage, lastRow);
      }, 500);
    }
  };
};

export const numericTypes = [
  'CALCULATION',
  'CUSTOM_PRODUCT',
  'FEET_INCH_1$$4INCH',
  'CUSTOM_ODOMETER',
  'INVENTORY',
  'NUMERIC',
  'CUSTOM_TOP_GAUGE_GROSS_BARREL',
  'CUSTOM_BOTTOM_GAUGE_GROSS_BARREL'
];

export const getDriverByIdOrName = (driver: any, driverList: any[]) => {
  if (driver?.id) return driver?.id;

  if (driver?.contact?.firstName && driver?.contact?.lastName) {
    const fullName = trim(driver?.contact?.firstName) + ' ' + trim(driver?.contact?.lastName);
    const driverFound = driverList.find((item: any) => {
      const splitDescription = item.description && String(item.description).split(',');
      const description = trim(splitDescription?.[1]) + ' ' + trim(splitDescription?.[0]);
      return description === fullName;
    });
    return driverFound?.id;
  }
  return null;
};

export const getPickUpsByIdFromAccount = async (
  id: number,
  setIsLoading?: (value: boolean) => void
): Promise<any[]> => {
  try {
    setIsLoading?.(true);
    const responsePickUp: any = await pickUpService.getById(id);
    setIsLoading?.(false);
    return [
      {
        ...responsePickUp?.data?.pickUpList?.[0],
        parentId: responsePickUp?.data?.id,
        extraColumns: { defaultDropoffList: responsePickUp?.data?.pickUpList?.[0].defaultDropOffList }
      }
    ];
  } catch (error) {
    setIsLoading?.(false);
    throw (error as Error).message;
  }
};

export const getDropOffByIdFromAccount = async (
  id: number,
  setIsLoading?: (value: boolean) => void
): Promise<any[]> => {
  try {
    setIsLoading?.(true);
    const responseDropOff: any = await dropOffService.getById(id);
    setIsLoading?.(false);
    return [{ ...responseDropOff?.data?.dropOffList?.[0], parentId: responseDropOff?.data?.id }];
  } catch (error) {
    setIsLoading?.(false);
    throw (error as Error).message;
  }
};

export const parsePickUpsToList = (data: any) => {
  const pickUpList: any[] = [];
  data?.forEach((accountObj: any) => {
    if (!!accountObj?.pickUpList) {
      accountObj.pickUpList?.forEach((pickUpObj: any) => {
        pickUpList.push({ ...pickUpObj, parentId: accountObj.id });
      });
    }
  });
  return pickUpList;
};

export const parseResultForSelect = (data: any, otherLabel = false) => {
  return data?.map((basic: any) => ({
    id: basic.id,
    value: basic.id,
    label: !otherLabel ? getPickUpContactInfo(basic) : getPickUpName(basic),
    status: basic.status,
    isSuspended: basic?.isSuspended,
    suspendedEffectiveDate: basic?.suspendedEffectiveDate,
    suspendedNotes: basic?.suspendedNotes,
    extraColumns: basic?.extraColumns,
    isBobTailLoad: basic?.isBobTailLoad,
    isSkipLocationOnTablet: basic?.extraColumns?.isSkipLocationOnTablet,
    additionalSearchText: basic?.extraColumns?.tankNumbers ?? '',
    defaultDriverId: basic?.extraColumns?.default_driver_id,
    parentId: basic.parentId
  }));
};

export const simpleParseResultSelect = (data: any) => {
  return data?.map((basic: any) => ({
    value: basic.id,
    label: basic.description,
    status: basic.status
  }));
};

export const loadsPickUpsFromAccount = async (
  basicListByTypeStoreForPickUp: BasicListByTypeStore,
  commodity: any,
  account: any,
  config?: any,
  pickupDepartureTime?: string
) => {
  await basicListByTypeStoreForPickUp.getPickUpsOrDropOffsByAccount(account, 'PICK_UP', [commodity], {
    params: {
      isIncludeHistoricalContract: true,
      dateForHistoricalContractValidation: pickupDepartureTime ? moment(pickupDepartureTime).format('YYYY-MM-DD') : '',
      ...config
    }
  });
  return basicListByTypeStoreForPickUp.pickUpsByAccount;
};

export const loadsDropOffsFromAccount = async (
  basicListByTypeStoreForDropOff: BasicListByTypeStore,
  commodity: any,
  account: any,
  config?: any
) => {
  await basicListByTypeStoreForDropOff.getPickUpsOrDropOffsByAccount(account, 'DROP_OFF', [commodity], {
    params: { isIncludeHistoricalContract: true, ...config }
  });
  return basicListByTypeStoreForDropOff.dropOffsByAccount;
};

export const getCommodityArrayParameter = (ids: any[]): string => {
  let parameters = '';
  ids.forEach(id => {
    parameters += `&commodityIds=${id}`;
  });
  return parameters;
};

export enum WorkShift {
  DAY,
  NIGHT,
  ANY
}

export enum DriverContractorType {
  COMPANY,
  CONTRACTOR
}

export const getUnselectedDrivers = (values: any, drivers: any): any => {
  const unselected = [] as IItemsList[];
  const { driversUnselected, terminal, workShift, driverGroup, contractor, type, selected } = values;
  driversUnselected.forEach((value: number | string) => {
    const { description: text, extraColumns } = drivers.get(parseInt(`${value}`, 10));
    const { terminalId, contractorId, isDay, isNight, driverGroupList } = extraColumns;
    if (terminal && terminalId !== terminal) return;
    if (contractor && contractorId !== contractor) return;
    if (driverGroup && !driverGroupList?.includes?.(driverGroup)) return;
    if (workShift === WorkShift.DAY && isNight) return;
    if (workShift === WorkShift.NIGHT && isDay) return;
    if (type === DriverContractorType.CONTRACTOR && !contractorId) return;
    if (type === DriverContractorType.COMPANY && contractorId) return;
    if (selected.some((item: any) => parseInt(item.value) === value)) return;

    unselected.push({ text, value: `${value}` });
  });
  return unselected.sort(({ text: a }, { text: b }) => (a > b ? 1 : -1));
};

export const getUnselectedTerminals = (form: any, optionTerminals: any): any => {
  const terminalList = form.getValues('terminalList');
  const unselected = [] as IItemsList[];

  optionTerminals.forEach(({ value, label }: any) => {
    if (terminalList.includes(value)) {
      return;
    }
    unselected.push({ text: label, value: `${value}` });
  });
  return unselected.sort(({ text: a }: any, { text: b }: any) => (a > b ? 1 : -1));
};

export const unselectedDriverGroups = (form: any, optionsDriverGroup: any): IItemsList[] => {
  const driverGroupList = form.getValues('driverGroupList');
  const unselected = [] as IItemsList[];

  optionsDriverGroup.forEach(({ value, label }: any) => {
    if (driverGroupList.includes(value)) {
      return;
    }
    unselected.push({ text: label, value: `${value}` });
  });
  return unselected.sort(({ text: a }: any, { text: b }: any) => (a > b ? 1 : -1));
};

export const unselectedDriverType = (form: any): IItemsList[] => {
  const driverTypeList = form.getValues('driverTypeList');
  const unselected = [] as IItemsList[];

  if (!driverTypeList?.includes(DriverContractorType.COMPANY)) {
    unselected.push({ text: 'COMPANY', value: `${DriverContractorType.COMPANY}` });
  }

  if (!driverTypeList?.includes(DriverContractorType.CONTRACTOR)) {
    unselected.push({ text: 'CONTRACTOR', value: `${DriverContractorType.CONTRACTOR}` });
  }
  return unselected.sort(({ text: a }: any, { text: b }: any) => (a > b ? 1 : -1));
};

export const selectedDriverType = (form: any): IItemsList[] => {
  return form
    .getValues('driverTypeList')
    ?.map((id: number) => {
      if (id === DriverContractorType.COMPANY) {
        return { text: 'COMPANY', value: `${DriverContractorType.COMPANY}` };
      } else if (id === DriverContractorType.CONTRACTOR) {
        return { text: 'CONTRACTOR', value: `${DriverContractorType.CONTRACTOR}` };
      }
    })
    .sort(({ text: a }: any, { text: b }: any) => (a > b ? 1 : -1));
};

export const loadDrivers = async (driverSetter: any, setter: Map<number, any>, callback?: any) => {
  try {
    const { data } = await basicListByTypeService.getDrivers([]);
    driverSetter([...data]);
    callback?.(
      data?.map((driver: any) => {
        const { id: value, description: label, commodityList } = driver;
        const driverGroupList = driver.extraColumns.driverGroupList?.map(({ id }: never) => id) ?? [];
        const extraColumns = { ...driver.extraColumns, driverGroupList };
        setter?.set(value, { ...driver, extraColumns });
        return { value, label, commodityList };
      }) || []
    );
  } catch (e) {
    throw new Error((e as Error).message);
  }
};

export const loadDriverGroup = async (setter: Map<number, any>, callback?: any, optionsCallback?: any) => {
  try {
    const { data } = await driverGroupService.getAll({});
    callback(data.map(({ id: value, name: label }) => ({ value, label })));
    optionsCallback?.(
      data?.map((driverGroup: any) => {
        const { id: value, name: label } = driverGroup;
        setter?.set(value, driverGroup);
        return { value, label };
      }) || []
    );
  } catch (e) {
    throw new Error((e as Error).message);
  }
};

export const loadRequirementGroups = async (
  loadRequirementGroupSetter: any,
  setter: Map<number, any>,
  callback?: any
) => {
  try {
    const { data } = await requirementGroupService.getGroupsBySourceType('CODE_TYPE_SET');
    loadRequirementGroupSetter([...data]);
    callback?.(
      data?.map((requirement: any) => {
        const { id: value, description: label } = requirement;
        setter?.set(value, { ...requirement });
        return { value, label };
      }) || []
    );
  } catch (e) {
    throw new Error((e as Error).message);
  }
};

export const loadTerminals = async (setter: Map<number, any>, callback?: any) => {
  try {
    const { data } = await basicListByTypeService.getTerminals([]);
    callback?.(
      data?.map((terminal: any) => {
        const { id: value, description: label } = terminal;
        setter?.set(value, terminal);
        return { value, label };
      }) || []
    );
  } catch (e) {
    throw new Error((e as Error).message);
  }
};

export const loadContractors = async (setter: Map<number, any>, callback?: any) => {
  try {
    const response = await basicListByTypeService.getContractors();
    callback?.(
      response?.data?.map((contractor: any) => {
        const { id: value, description: label } = contractor;
        setter?.set(value, contractor);
        return { value, label };
      }) || []
    );
  } catch (e) {
    throw new Error((e as Error).message);
  }
};

export const setDriverSelections = (
  getUnselectedDrivers: any,
  driver: any,
  formDriverController: any,
  formDriverContractorController: any
) => {
  const { driversSelected, driversUnselected } = getUnselectedDrivers();
  const drivers = driversSelected.map((id: number) => {
    const { description: text } = driver.get(id);
    return { text, value: `${id}` };
  });

  formDriverController.set('selected', drivers);
  formDriverController.set('driversUnselected', driversUnselected);
  formDriverContractorController.set('isOpen', true);
};

export const stopEdition = (setIsOpenPopOverV: any, isOpenPopOver: any, stopEditing: any) => {
  setIsOpenPopOverV?.(false);
  isOpenPopOver.current = false;
  stopEditing?.(true);
};
export const onBlur = (setIsOpenPopOverV: any, isOpenPopOver: any, stopEdition: any) => {
  if (!isOpenPopOver.current) {
    setIsOpenPopOverV?.(false);
    setTimeout(() => {
      stopEdition?.();
    }, 100);
  }
};
export const popOverIsOpen = (setIsOpenPopOverV: any, isOpenPopOver: any) => {
  setTimeout(() => {
    isOpenPopOver.current = true;
    setIsOpenPopOverV?.(true);
  }, 25);
};

export const isValidHttpURL = (value: string): boolean => {
  try {
    const newUrl = new URL(value);
    return newUrl.protocol === 'http:' || newUrl.protocol === 'https:';
  } catch (err) {
    return false;
  }
};

export const getNotes = (notes: Note[] | undefined | null): any => {
  const argNotes = notes || [];
  const newNotes = argNotes.map((item: any) => {
    if (item.mx_internal_uid) {
      return item;
    }
    return { ...item, mx_internal_uid: uniqueId('driverNotes_') };
  });
  return newNotes;
};

export const getToggleArchivedNotes = (selectedItem: any, notes: any[], showArchived: boolean): any => {
  if (selectedItem) {
    const newNotes = cloneDeep(notes);
    newNotes.some((item: any, index: number) => {
      const result = item.mx_internal_uid === selectedItem.mx_internal_uid;
      if (result) {
        const date = moment(moment().format('MM/DD/YYYY kk:mm')).toDate();
        const archived = !showArchived;
        newNotes[index] = {
          ...item,
          archived,
          lastModifiedDateTime: getDatesObject(date, date, true),
          isArchived: archived
        };
      }
      return result;
    });
    return newNotes;
  }
};

export const getEditNotes = (note: any, notes: Note[]): any => {
  const newNotes = cloneDeep(notes);
  newNotes.some((item: any, index: number) => {
    const result = item.mx_internal_uid === note.mx_internal_uid;
    if (result && newNotes[index]) {
      newNotes[index].notes = note.notes;
      const date = moment(moment().format('MM/DD/YYYY kk:mm')).toDate();
      newNotes[index].lastModifiedDateTime = getDatesObject(date, date, true);
    }
    return result;
  });
  return newNotes;
};

export const getGeneratePinnedBottomData = (event: ColumnApi, isActual: boolean): any => {
  let result: any = {};
  event?.getAllGridColumns()?.forEach((item: any) => {
    const colId = item.getColId();
    if (colId !== 'add' || colId !== 'delete') result[colId] = null;
    result.isActual = 'Total ' + (isActual ? 'System' : 'Driver Edited');
  });
  return result;
};

export const ticketImportBlockOptions = [
  {
    label: 'NONE',
    value: 'NONE'
  },
  {
    label: 'PICKUP',
    value: 'PICK_UP'
  },
  {
    label: 'DROP OFF',
    value: 'DROP_OFF'
  },
  {
    label: 'BOTH',
    value: 'BOTH'
  }
];

export const loadRequirementOptions = [
  {
    label: 'NONE',
    value: 'NONE'
  },
  {
    label: 'WARN',
    value: 'WARN'
  },
  {
    label: 'BLOCK',
    value: 'BLOCK'
  }
];

export const entityOptions = [
  {
    label: 'PICKUP',
    value: 'PICK_UP'
  },
  {
    label: 'DROP OFF',
    value: 'DROP_OFF'
  },
  {
    label: 'DRIVER',
    value: 'DRIVER'
  },
  {
    label: 'TRUCK/TRAILER',
    value: 'TRUCK_TRAILER'
  }
];

export const updateElementIntoList = (element: any, responseData: any, setResponseData: any): Promise<any> => {
  return new Promise(resolve => {
    const data = replaceTicketElementInList(element, responseData, 'id', 'ticketNo', 'dependencyRowIndex');
    setResponseData?.(data);
    resolve(true);
  });
};

export const replaceTicketElementInList = (
  elementsToBeUpdated: any[],
  responseData: any,
  key: string = 'id',
  ticketNo: string = 'ticketNo',
  dependencyRowIndex: string = 'dependencyRowIndex'
) => {
  const newResponseData = responseData?.reviewReconcileContainers?.map((el: any) => {
    const foundElement = elementsToBeUpdated?.find(
      element =>
        el?.[key] === element?.[key] &&
        (el?.[ticketNo] !== '' ? el?.[ticketNo] === element?.[ticketNo] : true) &&
        (el?.[dependencyRowIndex] !== '' ? el?.[dependencyRowIndex] === element?.[dependencyRowIndex] : true)
    );
    if (foundElement) return { ...foundElement };
    else return { ...el };
  });
  return { ...responseData, reviewReconcileContainers: newResponseData };
};

export const validateRequirements = async (dispatchStore: any, loadsList: any[], driver: any, data: any) => {
  dispatchStore?.setDriverToAssignLoad(driver);
  dispatchStore?.setLoadListAssign(loadsList);
  dispatchStore?.setLoadRequirementsValidatedResponse(data);
  dispatchStore?.setIsOpenAssignLoads(true);
};

export const getProfiles = (profileType: IViewTypes, set?: any) => {
  profileService.getAll({ profileType }).then(response => {
    set?.(
      response?.data?.map(item => {
        return { label: item.name, value: item.id };
      }) ?? []
    );
  });
};
